﻿import { Inject } from "@fiqas/angular-utils";
import { Utils } from "@fiqas/utils";
import "angular-resizable";
import { Subscription } from "rxjs";
import { ConversationSettings } from "../../constants";
import { DropdownEntry } from '../../controls/dropdownbox';
import { IIdentifiablePagedCollectionOf, IIdentityLink, IResourceLink, IResourceResolver } from "../../interfaces/iFqs";
import { Customer } from "../../models/customer";
import { CaseFile, CaseFileData } from "../../models/file";
import { CompactPerson, FiqasEmployee, Person } from "../../models/person";
import { RecordHistory } from "../../models/recordHistory";
import { ActionLinks, CaseCause, CaseOwnership, Correspondence, CorrespondenceType, FeedbackStatus, GeneralLink, HasActions, ResponsibleSDEmployee, ServiceCase } from '../../models/serviceCase';
import { AuthenticationService } from "../../services/authenticationService";
import { AuthorizationService } from "../../services/authorizationService";
import { CacheService } from "../../services/cache";
import { DialogService } from "../../services/dialogService";
import { FileService } from "../../services/fiqassistApi/FileService";
import { ServiceCaseService } from "../../services/fiqassistApi/serviceCaseService";
import { UserService } from "../../services/fiqassistApi/userService";
import { IsProcessingService } from "../../services/isProcessingService";
import { LogService } from "../../services/logService";
import { ServiceUtils } from "../../services/serviceBase";
import { TranslationService } from "../../services/TranslationService";
import { Application } from "../application";
import { ConversationItem } from "../general/conversation";
import { FeedbackStatusLink } from "../general/conversationentry";
import { FileItem } from "../general/fileDropdownList";
import { RichtextEditor } from "../general/richtexteditor";
import { StatusButton } from "../general/statusbuttons";
import { ManageWatchers } from "./manageWatchers";
import { SaveDialog } from "./savedialog";
import { ServiceCaseBase } from "./serviceCaseBase";


export type CaseType = "problem" | "request";

export abstract class ServiceCaseEdit<T extends ServiceCase> {
    public static template = "views/servicecase.edit.html";

    protected abstract application: Application;
    protected abstract Base: ServiceCaseBase<T>;
    protected abstract $router: ng.Router;
    public abstract casetype: CaseType;
    public statuslinks: StatusButton[];
    public feedbackLinks: FeedbackStatusLink[];
    private showAlerts: boolean;
    private showInternal: boolean;
    private CorrespondenceCheck: GeneralLink<boolean, void, void>;
    private _conversationItems: ConversationItem[];
    private subscriptions: Subscription[] = [];
    protected Conversation: ConversationItem[];
    protected Files: FileItem[];
    protected loadingData: boolean = false;
    protected initialized = false;

    private ctrlRichTextEditor: RichtextEditor;
    public activeTabIndex: number;

    public fiqasEmployees: Record<string, CompactPerson>;
    public fiqasEmployeesSelections: DropdownEntry<string>[] = [];

    public caseTypes: DropdownEntry<CaseType>[] = [
        { key: <CaseType>'bug', text: "Problem" },
        { key: <CaseType>'request', text: "Request" },
    ].sort((a, b) => a.text.localeCompare(b.text));

    public ownershipTypes: DropdownEntry<CaseOwnership>[] = [
        { key: <CaseOwnership>"serviceDesk", text: "Service desk" },
        { key: <CaseOwnership>"billingBureau", text: "Billing bureau" },
        { key: <CaseOwnership>"projectManagement", text: "Project management" },
        { key: <CaseOwnership>"sales", text: "Sales" }
    ].sort((a, b) => a.text.localeCompare(b.text));

    public caseCauseTypes: DropdownEntry<CaseCause>[] = [
        { key: <CaseCause>"billableSupport", text: "Billable support" },
        { key: <CaseCause>"configBug", text: "Config bug" },
        { key: <CaseCause>"toBeDetermined", text: "To be determined" },
        { key: <CaseCause>"custKnowlegdeIssue", text: "Customer knowledge issue" },
        { key: <CaseCause>"custData", text: "Customer data" },
        { key: <CaseCause>"custConfig", text: "Customer configuration" },
        { key: <CaseCause>"uat", text: "UAT" },
        { key: <CaseCause>"custVersionControlIssue", text: "Customer version control issue" },
        { key: <CaseCause>"custRequest", text: "Customer request" },
        { key: <CaseCause>"incorrectRequirements", text: "Incorrect requirements" },
        { key: <CaseCause>"changedRequirements", text: "Changed requirements" },
        { key: <CaseCause>"softwareBug", text: "Software bug" },
        { key: <CaseCause>"databaseIssue", text: "Database (script) issue" },
        { key: <CaseCause>"versionControlIssue", text: "Version control issue" },
        { key: <CaseCause>"other", text: "Other" },
        { key: <CaseCause>"paymentProcessing", text: "Payment processing" },
        { key: <CaseCause>"userMaintenance", text: "User maintenance" },
        { key: <CaseCause>"dBAActivitice", text: "DBA activities" },
        { key: <CaseCause>"usabilityImprovement", text: "Usability Improvement" },
        { key: <CaseCause>"network", text: "Network/Infrastructure" },
        { key: <CaseCause>"thirdParty", text: "Third party (not a FIQAS issue)" }
    ].sort((a, b) => a.text.localeCompare(b.text));

    public responsibleSDEmployeeTypes: DropdownEntry<ResponsibleSDEmployee>[] = [
        { key: <ResponsibleSDEmployee>"stefan", text: "Stefan van Roosmalen" },
        { key: <ResponsibleSDEmployee>"dave", text: "Dave Buskermolen" },
        { key: <ResponsibleSDEmployee>"ilse01", text: "Ilse Bakker" },
        { key: <ResponsibleSDEmployee>"ingeborg", text: "Ingeborg Bronkhorst" },
        { key: <ResponsibleSDEmployee>"tbd", text: "To be defined" },
        { key: <ResponsibleSDEmployee>"fintern", text: "Intern FIQAS" }
    ].sort((a, b) => a.text.localeCompare(b.text));

    public feedbackStates: DropdownEntry<FeedbackStatus>[] = [
        { key: <FeedbackStatus>"notRequired", text: "Not Required" },
        { key: <FeedbackStatus>"provided", text: "Provided" },
        { key: <FeedbackStatus>"requested", text: "Requested" }
    ].sort((a, b) => a.text.localeCompare(b.text));

    private hotkey: ng.hotkeys.Hotkey[];
    private tabsHotkeys: ng.hotkeys.Hotkey;

    constructor(
        protected authorizationService: AuthorizationService,
        protected serviceCaseService: ServiceCaseService,
        protected isProcessingService: IsProcessingService,
        protected TranslationService: TranslationService,
        private $auth: AuthenticationService,
        private $q: ng.IQService,
        private cacheService: CacheService,
        protected $timeout: ng.ITimeoutService,
        protected $modal: angular.ui.bootstrap.IModalService,
        private conversationSettings: ConversationSettings,
        protected logService: LogService,
        protected dialogManager: DialogService,
        private saveDialog: SaveDialog,
        protected $scope: ng.IScope,
        private $rootScope: angular.IRootScopeService,
        protected $element: ng.IRootElementService,
        private hotkeys: ng.hotkeys.HotkeysProvider,
        private fileService: FileService,
        private userService: UserService,
    ) {
        this.$scope.$on("angular-resizable.resizeStart", (event, args) => this.onResizeStart(event, args));
        this.$scope.$on("angular-resizable.resizing", (event, args) => this.onResizing(event, args));
        this.$scope.$on("angular-resizable.resizeEnd", (event, args) => this.onResizeEnd(event, args));
        $rootScope.$on('richTextEditorInitialized', (event, ctrlRichTextEditor) => {
            this.ctrlRichTextEditor = ctrlRichTextEditor;
            this.ctrlRichTextEditor.readOnly = this.readOnly;
        });


        hotkeys.bindTo($scope);
        this.hotkey = [this.tabsHotkeys = {
            combo: ['alt+1'],
            allowIn: ['INPUT', 'SELECT', 'TEXTAREA'],
            callback: (e) => { this.setActiveTab(e) }
        }, {
            combo: ['alt+2'],
            allowIn: ['INPUT', 'SELECT', 'TEXTAREA'],
            callback: (e) => { this.setActiveTab(e) },
        }
        ];

        const activeTab = this.cacheService.getValue<number>("servicecase.tabs.index");
        this.activeTabIndex = activeTab ? activeTab : 0;
    }

    private scrollbottom: number = null;
    public onResizeStart(event, args): void {
        this.scrollbottom = null;
        //console.log(`this.scrollbottom = null`);
        this.ajustDynamicHTMLLayout(0);
    }
    public onResizing(event, args): void {
        this.ajustDynamicHTMLLayout(0);
    }
    public onResizeEnd(event, args): void {
        this.ajustDynamicHTMLLayout(0);
    }

    public resetDynamicHTMLLayout() {
        this.scrollbottom = null;
    }

    public _deadlineDT: Date;
    public get deadlineDT(): Date {
        return this._deadlineDT;
    }
    public set deadlineDT(value: Date) {
        this._deadlineDT = value;
    }
    private deadlineThresholdModal = null;
    public async checkDeadlineChange(value: Date) {
        if (
            (!!value === false && !!this.ServiceCase.requestedDeadline == false)
            || Date.dateEquals(new Date(this.ServiceCase.requestedDeadline), value)
            || this.deadlineThresholdModal !== null
            || !!value && value < this._minDeadline
        ) {
            return false; // No change
        } else if (
            (value == null && !!this.ServiceCase.requestedDeadline)
            || value >= this._deadlineThreshold
        ) {
            return true; // Change, plus new value null or over threshold.
        } else {
            // Change, but date falls in threshold time
            let options: ng.ui.bootstrap.IModalSettings = {
                animation: false,
                templateUrl: 'handle_thresholddeadline.html',
                controller: ConfirmDeadlineComponent,
                controllerAs: '$modal',
                resolve: {
                    page: () => this
                }
            };
            this.deadlineThresholdModal = this.$modal.open(options);
            try {
                let result = await this.deadlineThresholdModal.result;
                if (result == "yes" && this.deadlineRemark && this.deadlineRemark.length > 1) {
                    return true;
                } else {
                    this.deadlineRemark = null;
                }
            } catch (_) {
            } finally {
                this.deadlineThresholdModal.close();
                this.deadlineThresholdModal = null;
            }
            return false;
        }
    }
    protected minRememberMe: Date;
    protected _minDeadline: Date;
    protected _deadlineThreshold: Date;
    public get deadlineThreshold(): Date { return this._deadlineThreshold; };
    public deadlineRemark: string = "";
    public deadlineBoxOpen() {
        this.deadlineToolboxIsOpen = true;
        this.deadlineTooltipText = this.deadlineToolboxIsOpen ? '' : `${this.TranslationService.Translate('DEADLINE_TOOLTIP')}${(this.deadlineDT) ? `: ${this.deadlineDT.toLocaleDateString('nl-NL')}` : ''}`;
    }
    public deadlineBoxClose() {
        this.deadlineToolboxIsOpen = false;
        this.deadlineTooltipText = this.deadlineToolboxIsOpen ? '' : `${this.TranslationService.Translate('DEADLINE_TOOLTIP')}${(this.deadlineDT) ? `: ${this.deadlineDT.toLocaleDateString('nl-NL')}` : ''}`;
    }
    public deadlineToolboxIsOpen: boolean = false;
    public deadlineTooltipText: string = this.deadlineToolboxIsOpen ? '' : `${this.TranslationService.Translate('DEADLINE_TOOLTIP')}${(this.deadlineDT) ? `: ${this.deadlineDT.toLocaleDateString('nl-NL')}` : ''}`;

    public ajustDynamicHTMLLayout(height: number) {
        let casetabs: HTMLElement = this.$element[0].getElementsByClassName('casetabs').item(0);
        let casechat: HTMLElement = this.$element[0].getElementsByClassName('casechat').item(0);
        let caseedit: HTMLElement = this.$element[0].getElementsByClassName('caseedit').item(0);
        let conversationHolder: HTMLElement = this.$element[0].getElementsByClassName('conversation-scroll').item(0);
        let scrollbottom: number;
        if (conversationHolder) {
            scrollbottom = (this.scrollbottom) ? this.scrollbottom : this.scrollbottom = conversationHolder.scrollHeight - conversationHolder.offsetHeight - conversationHolder.scrollTop;

            let conversation: HTMLElement = this.$element[0].getElementsByClassName('conversation-frame').item(0);
            if (height > 39) {
                conversation.style.paddingBottom = `${height - 39}px`;
            } else if (conversation.style.paddingBottom) {
                conversation.style.paddingBottom = null;
            }
        }
        if (!this.collapseInfo) {
            angular.element(casechat).css('top', (casetabs.clientHeight + 140) + "px");
        }
        if (conversationHolder) {
            conversationHolder.scrollTop = conversationHolder.scrollHeight - conversationHolder.offsetHeight - scrollbottom;
        }
    }

    private _collapseInfo: boolean;
    public get collapseInfo(): boolean {
        return this._collapseInfo;
    }
    public set collapseInfo(val: boolean) {
        this._collapseInfo = val;
        if (val) {
            angular.element(this.$element[0].getElementsByClassName('casechat').item(0)).css('top', null);
        } else {
            this.ajustDynamicHTMLLayout(0);
        }
    }

    public get allowShowInternal() {
        return this.authorizationService.currentRoleIn(["SD", "FQS"]);
    }

    public get allowInternal() {
        return this.authorizationService.currentRoleIn(["SD", "FQS"]);
    }

    public get allowExternal() {
        return this.authorizationService.currentRoleIn(["SD", "CUST"]);
    }

    public get isSDEmployee() {
        return this.authorizationService.currentRoleIn(["SD"]);
    }

    public get chatType() {
        if (this.allowInternal && this.allowExternal)
            return "all";
        if (this.allowInternal)
            return "internal";
        if (this.allowExternal)
            return "external";
        return "";
    }

    public get isTabPropertiesDisabled() {
        return false;
    }

    private alive: boolean;

    protected abstract onInit();
    protected abstract onDestroy();
    protected abstract async loadData();
    protected abstract async HandleNewConversationEntry(entry: string, internal: boolean);

    private async doLoadData() {
        try {
            this.loadingData = true;
            await this.loadData();
            this.initializePropertiesFormFields();
        } finally {
            this.loadingData = false;
        }
    }

    async $onInit() {
        this.alive = true;
        this.onInit();
        this.minRememberMe = Date.today().addDays(1);
        this.initialized = true;
        this.Conversation = [];
        this.Files = [];
        this.application.forcedCustomer = ' ';
        this.Base.isNew = false;
        this.Base.onTitleChanged = title => this.onTitleChanged(title);
        this.checkEdits();

        this.hotkey.forEach(key => {
            this.hotkeys.add(key);
        });

        var persons = await this.userService.getFiqasEmployees();
        this.fiqasEmployees = persons.items
            .filter(p => p.hasLogin && p.isFiqasEmployee)
            .sort((a, b) => a.name.trim() > b.name.trim() ? 1 : -1)
            .reduce((v, n) => {
                const key = !!n.id.ref ? n.id.ref : n.id._ref;
                v[key] = n;
                return v;
            }, {});

        const serviceCaseFiqasOwnerRef = this.ServiceCase && this.ServiceCase.fiqasOwnership && (!!this.ServiceCase.fiqasOwnership.ref ? this.ServiceCase.fiqasOwnership.ref : this.ServiceCase.fiqasOwnership._ref);
        this.fiqasEmployeesSelections = persons.items
            .filter(p => p.hasLogin && p.isFiqasEmployee && !p.isFormerEmployee || serviceCaseFiqasOwnerRef && serviceCaseFiqasOwnerRef == (!!p.id.ref ? p.id.ref : p.id._ref))
            .sort((a, b) => a.name.trim() > b.name.trim() ? 1 : -1)
            .map(p => <DropdownEntry<string>>{ key: !!p.id.ref ? p.id.ref : p.id._ref, text: p.name });

        const activeTab = this.cacheService.getValue<number>("servicecase.tabs.index");
        this.activeTabIndex = activeTab ? activeTab : 0;
    }

    $onDestroy() {
        this.alive = false;
        this.onDestroy();
        this.application.forcedCustomer = null;
        this.Base.Title = '';
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.cacheService.setValue("servicecase.tabs.index", this.activeTabIndex);

        this.hotkey.forEach(key => {
            this.hotkeys.del(key.combo);
        });
    }

    public get watcherCount() {
        return !this.ServiceCase ?
            "" :
            this.ServiceCase.watcherCount == 0 ?
                this.TranslationService.Translate("NO_WATCHER") :
                this.TranslationService.Translate(this.ServiceCase.watcherCount == 1 ? "{0}_WATCHER" : "{0}_WATCHERS").replace("{0}", this.ServiceCase.watcherCount.toString())
    }

    public get readOnly() {
        return this.Base.readOnly;
    }
    public get readOnlyChat() {
        return this.Base.readOnlyChat;
    }

    public get serviceCaseDisabled() {
        let serviceCaseDisabled = true;
        if (!this.ServiceCase) {
            return serviceCaseDisabled;
        }
        return this.ServiceCase.status === 'closed' || this.ServiceCase.status === 'cancelled';
    }

    public deSelectTab($event: any, selectedIndex: number) {
        if (selectedIndex === this.activeTabIndex) {
            return;
        }
        if (this.descriptionEditing || this._propertiesTabDirty) {
            if ($event) {
                $event.preventDefault();
            }
            this.saveDialog.Save().then(result => {
                if (result === SaveDialog.Discard) {
                    if (this._propertiesTabDirty) {
                        this.initializePropertiesFormFields();
                    } else if (this.descriptionEditing) {
                        this.ctrlRichTextEditor.cancel();
                        this.descriptionEditing = false;
                    }
                    this.activeTabIndex = selectedIndex;
                    if (selectedIndex > 0) {
                        this.collapseInfo = false;
                    }
                }

            }).catch(() => { return false; });
        } else {
            if (selectedIndex > 0) {
                this.collapseInfo = false;
            }
        }
    }

    private setActiveTab(event) {
        if (event && event.key && parseInt(event.key, 10)) {
            this.activeTabIndex = (event.key - 1);
        }
    }

    // SERVICECASE PROPERTIES (KENMERKEN) CODE - We keep the code together, for extraction to it's own component

    protected _propertiesTabDirty = false;

    public get propertiesTabFieldsDisabled() {
        return !this.isSDEmployee;
    }

    public get propertiesTabCancelButtonDisabled() {
        return !this.isSDEmployee || !this._propertiesTabDirty;
    }

    public get propertiesTabSaveButtonEnabled() {

        const elRememberMeDate = this.$element[0].querySelector('#rememberMeDate input');
        let validDate: boolean = true;
        if (elRememberMeDate) {
            validDate = !elRememberMeDate.classList.contains('ng-invalid-date');
        }
        return validDate && this.isSDEmployee && this._propertiesTabDirty;
    }

    public caseType: any = [];
    public caseFeedbackStatus: Array<FeedbackStatus> = [];
    public casePropsFiqasOwnership: Array<string> = [];
    public casePropsCaseOwnership: Array<CaseOwnership> = [];
    public casePropsCaseCause: Array<CaseCause> = [];
    public casePropsResponsibleSDEmployee: Array<ResponsibleSDEmployee> = [];
    public casePropsActualHours: number = 0;
    public casePropsBudgetIndication: number = 0;
    public casePropsApprovedHoursMin: number = 0;
    public casePropsApprovedHoursMax: number = 0;
    public casePropsApprovedHoursCommentBySD: string = null;
    public casePropsSpTsTask: string = null;
    public casePropsRememberMeDate: Date;
    public casePropsRememberMeReason: string = null;
    public casePropsIgnoreCaseMemo: string = null;
    public casePropsParent: string = null;

    private initializePropertiesFormFields(): void {
        if (this.ServiceCase && this.ServiceCase.fiqasOwnership) {
            const ref = !!this.ServiceCase.fiqasOwnership.ref ? this.ServiceCase.fiqasOwnership.ref : this.ServiceCase.fiqasOwnership._ref;
            if (this.fiqasEmployees && this.fiqasEmployees[ref] && this.fiqasEmployees[ref].isFormerEmployee) this.fiqasEmployeesSelections.push({ key: ref, text: this.fiqasEmployees[ref].name });
            this.casePropsFiqasOwnership[0] = ref;
        } else {
            this.casePropsFiqasOwnership = [];
        }
        if (this.ServiceCase.caseOwnership) {
            this.casePropsCaseOwnership[0] = this.ServiceCase.caseOwnership;
        } else {
            this.casePropsCaseOwnership = [];
        }
        if (this.ServiceCase.caseCause) {
            this.casePropsCaseCause[0] = this.ServiceCase.caseCause;
        } else {
            this.casePropsCaseCause = [];
        }
        if (this.ServiceCase.responsibleSDEmployee) {
            this.casePropsResponsibleSDEmployee[0] = this.ServiceCase.responsibleSDEmployee;
        } else {
            this.casePropsResponsibleSDEmployee = ['tbd'];
        }
        if (this.ServiceCase.type) {
            this.caseType[0] = this.ServiceCase.type;
        } else {
            this.caseType = [];
        }
        if (this.ServiceCase.feedbackStatus) {
            this.caseFeedbackStatus[0] = this.ServiceCase.feedbackStatus;
        } else {
            this.caseFeedbackStatus = [];
        }

        this.casePropsParent = this.ServiceCase.parentCaseNumber;
        this.casePropsBudgetIndication = this.ServiceCase.budgetIndication;
        this.casePropsApprovedHoursMin = this.ServiceCase.approvedHoursMin;
        this.casePropsApprovedHoursMax = this.ServiceCase.approvedHoursMax;
        this.casePropsApprovedHoursCommentBySD = this.ServiceCase.approvedHoursCommentBySD;
        this.casePropsActualHours = this.ServiceCase.actualHoursSpent;
        this.casePropsIgnoreCaseMemo = this.ServiceCase.ignoreChatMemoSequence;

        this.casePropsSpTsTask = this.ServiceCase.spTsTask;
        this.casePropsRememberMeDate = this.ServiceCase.rememberMeDate ? new Date(this.ServiceCase.rememberMeDate).clone() : null;
        this.casePropsRememberMeReason = this.ServiceCase.rememberMeReason;

        this._propertiesTabDirty = false;
        this.setDirty();
    }

    private setPropertiesTabState() {

        if (this.casePropsCaseOwnership[0] === this.ServiceCase.caseOwnership &&
            this.casePropsCaseCause[0] === this.ServiceCase.caseCause &&
            this.caseFeedbackStatus[0] === this.ServiceCase.feedbackStatus &&
            this.casePropsFiqasOwnership[0] === (this.ServiceCase.fiqasOwnership && (!!this.ServiceCase.fiqasOwnership.ref ? this.ServiceCase.fiqasOwnership.ref : this.ServiceCase.fiqasOwnership._ref)) &&
            this.caseType[0] === this.ServiceCase.type &&
            (this.casePropsResponsibleSDEmployee[0] === this.ServiceCase.responsibleSDEmployee || !this.ServiceCase.responsibleSDEmployee && this.casePropsResponsibleSDEmployee.length && this.casePropsResponsibleSDEmployee[0] === 'tbd') &&
            (this.casePropsActualHours || 0) === (this.ServiceCase.actualHoursSpent || 0) &&
            (this.casePropsBudgetIndication || 0) === (this.ServiceCase.budgetIndication || 0) &&
            (this.casePropsApprovedHoursMin || 0) === (this.ServiceCase.approvedHoursMin || 0) &&
            (this.casePropsApprovedHoursMax || 0) === (this.ServiceCase.approvedHoursMax || 0) &&
            (this.casePropsApprovedHoursCommentBySD || '').trim() === (this.ServiceCase.approvedHoursCommentBySD || '').trim() &&
            (this.casePropsSpTsTask || '').trim() === (this.ServiceCase.spTsTask || '').trim() &&
            Date.dateEquals(this.casePropsRememberMeDate, this.ServiceCase.rememberMeDate ? new Date(this.ServiceCase.rememberMeDate).clone() : null) &&
            (this.casePropsRememberMeReason || '').trim() === (this.ServiceCase.rememberMeReason || '').trim() &&
            (this.casePropsIgnoreCaseMemo || '').trim() === (this.ServiceCase.ignoreChatMemoSequence || '').trim() &&
            (this.casePropsParent || '').trim() === (this.ServiceCase.parentCaseNumber || '').trim()
        ) {
            this._propertiesTabDirty = false;
        } else {
            this._propertiesTabDirty = true;
        }
        this.setDirty();
    }

    public feedbackStateSelected(type: any) {
        if (type && this.caseFeedbackStatus !== type) {
            this.caseFeedbackStatus = type;
            this.setPropertiesTabState();
        }
    }

    public caseFiqasOwnershipSelected(type: string[]) {
        // filter out formerEmployees when saving
        // type = type.filter(x => this.fiqasEmployees[x].isFiqasEmployee && !this.fiqasEmployees[x].isFormerEmployee)
        if (type && this.casePropsFiqasOwnership !== type) {
            this.casePropsFiqasOwnership = type;
            this.setPropertiesTabState();
        }
    }

    public caseTypeSelected(type: CaseType) {
        if (type && this.caseType !== type) {
            this.caseType = type;
            this.setPropertiesTabState();
        }
    }

    public ownershipSelected(type: CaseOwnership[]) {
        if (type && this.casePropsCaseOwnership !== type) {
            this.casePropsCaseOwnership = type;
            this.setPropertiesTabState();
        }
    }

    public caseCauseSelected(type: CaseCause[]) {
        if (type && this.casePropsCaseCause !== type) {
            this.casePropsCaseCause = type;
            this.setPropertiesTabState();
        }
    }

    public responsibleSDEmployeeSelected(type: ResponsibleSDEmployee[]) {
        if (type && this.casePropsResponsibleSDEmployee !== type) {
            this.casePropsResponsibleSDEmployee = type;
            this.setPropertiesTabState();
        }
    }

    public budgetIndicationChanged(value: any) {
        if (value == "-") {
            value = undefined;
        }
        if ((value !== undefined) && this.casePropsBudgetIndication !== Number(value)) {
            this.casePropsBudgetIndication = Number(value);
            this.setPropertiesTabState();
        }
    }

    public approvedHoursMinChanged(value: any) {
        if (value == "-") {
            value = undefined;
        }
        if ((value !== undefined) && this.casePropsApprovedHoursMin !== Number(value)) {
            this.casePropsApprovedHoursMin = Number(value);
            this.setPropertiesTabState();
        }
    }

    public approvedHoursMaxChanged(value: any) {
        if (value == "-") {
            value = undefined;
        }
        if ((value !== undefined) && this.casePropsApprovedHoursMax !== Number(value)) {
            this.casePropsApprovedHoursMax = Number(value);
            this.setPropertiesTabState();
        }
    }

    public approvedHoursCommentBySDChanged(value: any) {
        if ((value !== undefined) && this.casePropsApprovedHoursCommentBySD !== value) {
            this.casePropsApprovedHoursCommentBySD = value;
            this.setPropertiesTabState();
        }
    }

    public spTsTaskChanged(value: any) {
        if ((value !== undefined) && this.casePropsSpTsTask !== value) {
            this.casePropsSpTsTask = value;
            this.setPropertiesTabState();
        }
    }

    public rememberMeDateChanged(date: any) {
        if (!(Date.dateEquals(this.casePropsRememberMeDate, date))) {
            this.casePropsRememberMeDate = date;
            this.setPropertiesTabState();
        }
    }

    public rememberMeReasonChanged(value: any) {
        if ((value !== undefined) && this.casePropsRememberMeReason !== value) {
            this.casePropsRememberMeReason = value;
            this.setPropertiesTabState();
        }
    }

    public ignoreCaseMemoChanged(value: any) {
        if ((value !== undefined) && this.casePropsIgnoreCaseMemo !== value) {
            this.casePropsIgnoreCaseMemo = value;
            this.setPropertiesTabState();
        }
    }

    public parentChanged(value: any) {
        if ((value !== undefined) && this.casePropsParent !== value) {
            this.casePropsParent = value;
            this.setPropertiesTabState();
        }
    }

    private populateServiceCaseProperties(): void {
            const fiqasEmployee = this.fiqasEmployees[this.casePropsFiqasOwnership[0]];
            if (!!fiqasEmployee && !!fiqasEmployee.id) {
                this.ServiceCase.fiqasOwnership = fiqasEmployee.id;
            } else {
                this.ServiceCase.fiqasOwnership = undefined
            }

        this.ServiceCase.feedbackStatus = this.caseFeedbackStatus[0];
        this.ServiceCase.ignoreChatMemoSequence = this.casePropsIgnoreCaseMemo;
        this.ServiceCase.caseOwnership = this.casePropsCaseOwnership[0];
        this.ServiceCase.caseCause = this.casePropsCaseCause[0];
        this.ServiceCase.responsibleSDEmployee = this.casePropsResponsibleSDEmployee[0];
        this.ServiceCase.budgetIndication = this.casePropsBudgetIndication;
        this.ServiceCase.approvedHoursMin = this.casePropsApprovedHoursMin;
        this.ServiceCase.approvedHoursMax = this.casePropsApprovedHoursMax;
        this.ServiceCase.approvedHoursCommentBySD = this.casePropsApprovedHoursCommentBySD;
        this.ServiceCase.spTsTask = this.casePropsSpTsTask;
        this.ServiceCase.rememberMeDate = this.casePropsRememberMeDate ? this.casePropsRememberMeDate.toISODateString() : null;
        this.ServiceCase.rememberMeReason = this.casePropsRememberMeReason;
        this.ServiceCase.type = this.caseType[0];
        this.ServiceCase.ignoreMailing = true;
        this.ServiceCase.parentCaseNumber = this.casePropsParent;

    }

    public async saveProperties() {

        this.isProcessingService.StartProcessing();
        try {

            this.populateServiceCaseProperties();

            await this.serviceCaseService.UpdateCase<T>(this.ServiceCase.id, this.ServiceCase);
            await this.ReloadServiceCase();
        } catch (error) {
            if (ServiceUtils.IsUnauthorizedResponse(error))
                return;
            this.application.setSeqUrl(error);
            this.logService.error('ServiceCaseEdit', 'Error saving reference', error);
            this.dialogManager.showErrorDialog('error', 'ERROR', 'EDIT_REFERENCE_CASE_ERROR', { errorResult: error, translate: true });
        } finally {
            this.isProcessingService.StopProcessing();
        }
    }

    public clearProperties() {
        this.initializePropertiesFormFields();
    }


    // END NEW

    public async onTitleChanged(title: string) {
        this.isProcessingService.StartProcessing();
        try {
            this.ServiceCase.title = title;
            await this.serviceCaseService.UpdateCase<T>(this.ServiceCase.id, this.ServiceCase);
            await this.ReloadServiceCase();
        } catch (error) {
            if (ServiceUtils.IsUnauthorizedResponse(error))
                return;

            this.application.setSeqUrl(error);
            this.logService.error('ServiceCaseEdit', 'Error saving title', error);
            this.dialogManager.showErrorDialog('error', 'ERROR', 'EDIT_TITLE_ERROR', { errorResult: error, translate: true });
        } finally {
            this.isProcessingService.StopProcessing();
        }
    }

    public get watched() {
        if (this.ServiceCase && this.ServiceCase.actionLinks && this.ServiceCase.actionLinks.generalLinks) {
            if (!this.ServiceCase.actionLinks.generalLinks.watch && !this.ServiceCase.actionLinks.generalLinks.unwatch)
                return undefined;
            return !!this.ServiceCase.actionLinks.generalLinks.unwatch;
        }
        return undefined;
    }

    private _enableWatch: boolean = true;
    public set enableWatch(val: boolean) {
        this._enableWatch = val;
    }
    public get enableWatch(): boolean {
        return !this.readOnlyChat && this._enableWatch;
    }
    public async toggleWatch() {
        if (!this.loadingData && this.enableWatch) {
            try {
                this.enableWatch = false;
                this.isProcessingService.StartProcessing();
                let act = this.ServiceCase.actionLinks.generalLinks.watch || this.ServiceCase.actionLinks.generalLinks.unwatch;
                await act.action();
                await this.ReloadServiceCase();
            } finally {
                this.isProcessingService.StopProcessing();
                this.enableWatch = true;
            }
        }
    }

    public newFileTooltip: boolean;
    public newFileTooltext: string;

    private _timer: ng.IPromise<any>;
    private _notchanged: number = 0;
    private async checkEdits() {
        this._notchanged++;
        if (this.alive && this.conversationSettings.PeriodiclyCheckForChanges) {
            let caseChangedAction = Utils.getNestedPropertyValue(this, ctrl => ctrl.ServiceCase.actionLinks.generalLinks.hasChanged);
            if (caseChangedAction && !this.isProcessingService.IsProcessing) {
                let caseChanged = await caseChangedAction.action();
                if (caseChanged) {
                    this.ReloadServiceCase();
                    this._notchanged = 0;
                }
            }
            if (this.ServiceCase && this.CorrespondenceCheck && !this.isProcessingService.IsProcessing) {
                let correspondesChanged = await this.CorrespondenceCheck.action();
                if (correspondesChanged) {
                    this.GetHistoryAndCorrespondence();
                    this._notchanged = 0;
                }
            }
            let timeOutInSeconds = (this._notchanged < this.conversationSettings.PeriodsWithNoChanges) ? this.conversationSettings.ShortPeriodInSeconds : this.conversationSettings.LongPeriodInSeconds;

            this._timer = this.$timeout(() => this.checkEdits(), timeOutInSeconds * 1000);
        }
    }

    private showAlertsChanged(value: boolean) {
        this.showAlerts = value;
        this.setConversation();
    }

    private showInternalChanged(value: boolean) {
        this.showInternal = value;
        this.setConversation();
    }

    private setConversation() {
        this.Conversation = !this._conversationItems ? this._conversationItems :
            this._conversationItems
                .filter(i =>
                    i.direction != 'none'
                    || (this.showAlerts && i.direction == 'none' && i.class != 'internal')
                    || (this.showInternal && i.direction == 'none' && i.class == 'internal')
                );
    }

    public async $routerOnActivate(next?: angular.ComponentInstruction, prev?: angular.ComponentInstruction) {
        await this.authorizationService.roleLoaded;
        let idUrl = next.params.id ? atob(next.params.id) : null;
        let id = null;
        if (idUrl)
            id = { href: idUrl, fullHref: idUrl };
        let caseid = parseInt(next.params.number);
        if (isNaN(caseid))
            return false;

        try {
            this.isProcessingService.StartProcessing();
            if (id && (!this.ServiceCase || !this.ServiceCase.id || this.ServiceCase.id.fullHref != id.fullHref)) {
                try {
                    this.ServiceCase = await this.serviceCaseService.GetById(id, true) as T;
                } catch (x) {
                    if (caseid) {
                        this.ServiceCase = await this.serviceCaseService.GetByNumber(caseid, true) as T;
                    } else
                        throw x;
                }
            } else if ((!this.ServiceCase && caseid) || parseInt(this.ServiceCase.caseNumber) != caseid) {
                this.ServiceCase = await this.serviceCaseService.GetByNumber(caseid, true) as T;
            } else if (!this.ServiceCase) {
                return false;
            }

            this.Base.updatePrevNext(caseid.toString(), id);
            this.doLoadData();
        } catch (ex) {
            this.logService.error('ServiceCaseEdit', 'Error loading data', ex);
            return false;
        } finally {
            this.isProcessingService.StopProcessing();
        }
        return true;
    }

    public async $routerOnReuse(next?: angular.ComponentInstruction, prev?: angular.ComponentInstruction) {
        await this.authorizationService.roleLoaded;
    }

    protected async ReloadServiceCase() {
        this.isProcessingService.StartProcessing();
        try {
            if (this.ServiceCase) {
                this.ServiceCase = await this.serviceCaseService.GetById<T>(this.ServiceCase.id);
                await this.doLoadData();
            }
        } catch (e) {
            this.logService.error('ServiceCaseEdit', 'Error reloading servicecase', e);
        } finally {
            this.isProcessingService.StopProcessing();
        }
    }

    protected async GetHistoryAndCorrespondence(getHistory = true, getCorrespondence = true) {
        this.isProcessingService.StartProcessing();
        try {
            let [correspondence, history] = await Promise.all([
                getCorrespondence ? this.serviceCaseService.GetIIdentifiablePagedCollectionPropertyOf(this.ServiceCase.correspondence) : new Promise<IIdentifiablePagedCollectionOf<Correspondence>>(r => r(null)),
                getHistory ? this.serviceCaseService.GetIIdentifiableCollectionPropertyOf(this.ServiceCase.history) : new Promise<IIdentifiablePagedCollectionOf<RecordHistory>>(r => r(null))]);

            let actions: ActionLinks = getCorrespondence ? (<HasActions>(<any>correspondence)).actionLinks : null;
            this.CorrespondenceCheck = actions && actions.generalLinks && actions.generalLinks.hasChanged ? actions.generalLinks.hasChanged : null;
            const escapedCorrespondence = getCorrespondence ? correspondence.items : []
            escapedCorrespondence.forEach(item => { item.originalContent = item.content; item.content = (document.createElement('div').appendChild(document.createTextNode(item.content)).parentNode as any).innerHTML })
            await this.loadCorrespondence(
                escapedCorrespondence,
                getHistory ? history.items : []);
            this.setConversation();
        } catch (e) {
            this.logService.error('ServiceCaseEdit', 'Error getting correspondence', e);
        } finally {
            this.isProcessingService.StopProcessing();
        }
    }

    protected async GetFiles() {
        this.isProcessingService.StartProcessing();
        try {
            let p = await this.serviceCaseService.GetIIdentifiableCollectionPropertyOf(this.ServiceCase.files, true);
            this.loadFiles(p.items);
        } catch (e) {
            this.logService.error('ServiceCaseEdit', 'Error getting files', e);
        } finally {
            this.isProcessingService.StopProcessing();
        }
    }

    private correspondence: Correspondence[];
    private history: RecordHistory[];
    private async loadCorrespondence(correspondence: Correspondence[], history: RecordHistory[]) {
        await this.TranslationService.onLoaded;
        if (correspondence === null) {
            correspondence = this.correspondence;
        } else {
            this.correspondence = correspondence;
        }
        if (history === null) {
            history = this.history;
        } else {
            this.history = history;
        }
        let user = await this.application.user;
        let cust: Customer = await this.ServiceCase.customer.value;
        let conv = correspondence.map<ConversationItem>((c) => {
            return {
                id: c.id,
                person: c.createdBy,
                contactPerson: c.createdById,
                phone: (!c.bySupportEmployee) ? (cust.phone && cust.phone.trim().length > 0) ? `${cust.name}: ${cust.phone}` : `${cust.name}` : "",
                timestamp: new Date(c.createdOn),
                text: c.content,
                direction: (c.correspondenceType == "internal" ? 'none' :
                    (c.byCustomer || c.byInternalCustomer) && this.authorizationService.currentRoleIn(["CUST"])
                        || !c.byCustomer && !c.byInternalCustomer && (this.authorizationService.currentRoleIn(["SD", "FQS"]))
                        ? 'out' : 'in'),
                class: c.correspondenceType,
                fromFiqas: !c.byCustomer && !c.byInternalCustomer,
                fromSupport: c.bySupportEmployee,
                item: c
            }
        }).concat(history.map<ConversationItem>((h) => {
            return {
                id: h.id,
                person: h.modifiedBy,
                contactPerson: h.modifiedById,
                phone: null,
                timestamp: new Date(h.modifiedOn),
                text: this.generateHistoryText(h),
                direction: "none",
                class: h.itemType.toLowerCase(),
                item: h
            }
        })).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        this._conversationItems = conv;
    }

    private async onConversationTypeChange(id: IIdentityLink) {
        var correspondence = this._conversationItems.filter(i => i.id && i.id.equals(id)).shift().item as Correspondence;
        var item = {
            type: correspondence.correspondenceType,
            originalContent: correspondence.originalContent,
            text: correspondence.content,
            person: correspondence.createdBy,
            timestamp: correspondence.createdOn
        };
        let options: ng.ui.bootstrap.IModalSettings = {
            animation: false,
            templateUrl: 'correspondencetypechange.html',
            controller: ChangeCorrespondenceTypeComponent,
            controllerAs: '$ctrl',
            resolve: {
                item: () => item
            }
        };
        try {
            let m = this.$modal.open(options);
            let x = await m.result
            if (x == 'yes') {
                this.isProcessingService.StartProcessing();
                try {
                    if (correspondence.correspondenceType == item.type)
                        return;

                    correspondence.correspondenceType = item.type;
                    correspondence.content = item.originalContent;
                    await this.serviceCaseService.UpdateCorrespondence(correspondence);
                    this.ajustDynamicHTMLLayout(0);
                    await this.GetHistoryAndCorrespondence();
                    this._notchanged = 0;
                    if (this._timer !== undefined)
                        this.$timeout.cancel(this._timer);
                    this._timer = this.$timeout(() => { this.checkEdits(); }, 500);
                } catch (error) {
                    if (ServiceUtils.IsUnauthorizedResponse(error))
                        return;
                    this.application.setSeqUrl(error);
                    this.logService.error('ServiceCaseEdit', 'Error changing correspondence type', error);
                    this.dialogManager.showErrorDialog('error', 'ERROR', 'ADD_CORRESPONDENCE_CASE_ERROR', { errorResult: error, translate: true });
                } finally {
                    this.isProcessingService.StopProcessing();
                }
            }
        } catch (_) {

        }
    }

    private translatableProperties: string[] = [
        "CasePriority",
        "CaseSeverity",
        "CaseStatus",
        "WorkAroundYN",
        "Environment"
    ];

    private checkTranslate(itemname: string, content: string) {
        if (this.translatableProperties.indexOf(itemname) > -1) {
            return this.TranslationService.Translate(content.replace(/ /g, ""));
        } else if (itemname === "RequestedDeadline" && content) {
            var date = new Date(content.length == 8 ? `${content.substr(0, 4)}-${content.substr(4, 2)}-${content.substr(6, 2)}` : content);
            if (date.isValid())
                return date.toLocaleDateString();
        }
        return content;
    }

    private generateHistoryText(h: RecordHistory) {
        switch (h.itemType) {
            case "title": {
                if (h.oldContent !== undefined && h.oldContent !== null && h.oldContent.trim() !== ""
                    && h.newContent !== undefined && h.newContent !== null && h.newContent.trim() !== "") {
                    return this.TranslationService.Translate("CHANGED_{0}_FROM_{1}_TO_{2}")
                        .replace("{0}", `<span class="itemname">${desc != h.itemName ? desc : h.itemDesc}</span>`)
                        .replace("{1}", `<span class="oldvalue">${this.checkTranslate(h.itemName, h.oldContent)}</span>`)
                        .replace("{2}", `<span class="newvalue">${this.checkTranslate(h.itemName, h.newContent)}</span>`);
                } else if (h.newContent !== undefined && h.newContent !== null && h.newContent.trim() !== "") {
                    return this.TranslationService.Translate("CHANGED_{0}_TO_{1}")
                        .replace("{0}", `<span class="itemname">${desc != h.itemName ? desc : h.itemDesc}</span>`)
                        .replace("{1}", `<span class="newvalue">${this.checkTranslate(h.itemName, h.newContent)}</span>`);
                }
                return this.TranslationService.Translate("CHANGED_{0}")
                    .replace("{0}", `<span class="itemname">${this.TranslationService.Translate(h.itemType.toUpperCase())}</span>`);
            }
            case "property": {
                var desc = this.TranslationService.Translate(h.itemName);
                if (h.oldContent !== undefined && h.oldContent !== null && h.oldContent.trim() !== ""
                    && h.newContent !== undefined && h.newContent !== null && h.newContent.trim() !== "") {
                    return this.TranslationService.Translate("CHANGED_{0}_FROM_{1}_TO_{2}")
                        .replace("{0}", `<span class="itemname">${desc != h.itemName ? desc : h.itemDesc}</span>`)
                        .replace("{1}", `<span class="oldvalue">${this.checkTranslate(h.itemName, h.oldContent)}</span>`)
                        .replace("{2}", `<span class="newvalue">${this.checkTranslate(h.itemName, h.newContent)}</span>`);
                } else if (h.newContent !== undefined && h.newContent !== null && h.newContent.trim() !== "") {
                    return this.TranslationService.Translate("CHANGED_{0}_TO_{1}")
                        .replace("{0}", `<span class="itemname">${desc != h.itemName ? desc : h.itemDesc}</span>`)
                        .replace("{1}", `<span class="newvalue">${this.checkTranslate(h.itemName, h.newContent)}</span>`);
                }
                return this.TranslationService.Translate("CLEARED_{0}_{1}")
                    .replace("{0}", `<span class="itemname">${desc != h.itemName ? desc : h.itemDesc}</span>`)
                    .replace("{1}", `<span class="newvalue">${this.checkTranslate(h.itemName, h.oldContent)}</span>`);
            }
            case "document": {
                if (Number(h.newContent) == 0 && Number(h.oldContent) == 1) {
                    return this.TranslationService.Translate("ATTACHMENT_{0}_REMOVED")
                        .replace("{0}", `<span class="itemname">${h.itemDesc || h.itemName}</span>`);
                } else if (Number(h.newContent) == 1 && Number(h.oldContent) == 0) {
                    return this.TranslationService.Translate("ATTACHMENT_{0}_ADDED")
                        .replace("{0}", `<span class="itemname">${h.itemDesc || h.itemName}</span>`);
                }
            }
            case "feedbackStatus": {
                switch (h.newContent) {
                    case "FeedBackRequested":
                        return this.TranslationService.Translate("HAS_REQUESTED_FEEDBACK");
                    case "FeedBackProvided":
                        return this.TranslationService.Translate("HAS_PROVIDED_FEEDBACK");
                    case "NoFeedBackRequired":
                        return this.TranslationService.Translate("DOES_NOT_REQUIRE_FEEDBACK");
                }
            }
            case "watch": {
                let reversedDesc = h.itemDesc.split(',').reverse().join(' ').trim();
                if (Number(h.newContent) == 1 && Number(h.oldContent) == 0) {
                    if (h.itemName !== reversedDesc) {
                        return this.TranslationService.Translate("ADD_WATCH_{0}")
                            .replace("{0}", `<span class="itemname">${reversedDesc}</span>`);
                    } else {
                        return this.TranslationService.Translate("WATCHING");
                    }
                } else if (Number(h.newContent) == 0 && Number(h.oldContent) == 1) {
                    if (h.itemName !== reversedDesc) {
                        return this.TranslationService.Translate("REMOVED_WATCH_{0}")
                            .replace("{0}", `<span class="itemname">${reversedDesc}</span>`);
                    } else {
                        return this.TranslationService.Translate("UNWATCHED");
                    }
                }
            }
            default:
                return this.TranslationService.Translate("CHANGED_{0}")
                    .replace("{0}", `<span class="itemname">${this.TranslationService.Translate(h.itemType.toUpperCase())}</span>`);
        }
    }
    private loadFiles(data: CaseFile[]) {
        this.Files = data.map<FileItem>(i => {
            return {
                id: i.id,
                description: i.description,
                date: new Date(i.createdOn),
                createdBy: i.createdBy
            };
        });
    }

    private async editWatchlist() {
        let options: ng.ui.bootstrap.IModalSettings = {
            animation: false,
            templateUrl: '/views/manage.watchlist.html',
            controller: ManageWatchers,
            controllerAs: '$ctrl',
            resolve: {
                page: () => this,
                application: () => this.application,
                enableWatch: () => this.enableWatch && !this.readOnly,
                serviceCase: () => () => this.ServiceCase,
                reload: () => () => this.ReloadServiceCase()
            }
        };
        try {
            let m = this.$modal.open(options);
            let x = await m.result
        } catch (_) {

        }
    }

    public async descriptionChanged(source: RichtextEditor, value: string) {
        if (this.readOnly)
            return;
        this.isProcessingService.StartProcessing();
        try {
            this.ServiceCase.description = value;
            await this.serviceCaseService.UpdateCase<T>(this.ServiceCase.id, this.ServiceCase);
            await this.ReloadServiceCase();
        } catch (error) {
            if (ServiceUtils.IsUnauthorizedResponse(error))
                return;
            this.application.setSeqUrl(error);
            this.logService.error('ServiceCaseEdit', 'Error saving description', error);
            this.dialogManager.showErrorDialog('error', 'ERROR', 'EDIT_DESCRIPTION_CASE_ERROR', { errorResult: error, translate: true });
        } finally {
            this.isProcessingService.StopProcessing();
        }
    }

    public async referenceChanged(val: string) {
        if (this.readOnly)
            return;
        this.isProcessingService.StartProcessing();
        try {
            this.ServiceCase.customerReference = val;
            await this.serviceCaseService.UpdateCase<T>(this.ServiceCase.id, this.ServiceCase);
            await this.ReloadServiceCase();
        } catch (error) {
            if (ServiceUtils.IsUnauthorizedResponse(error))
                return;
            this.application.setSeqUrl(error);
            this.logService.error('ServiceCaseEdit', 'Error saving reference', error);
            this.dialogManager.showErrorDialog('error', 'ERROR', 'EDIT_REFERENCE_CASE_ERROR', { errorResult: error, translate: true });
        } finally {
            this.isProcessingService.StopProcessing();
        }
    }

    private descriptionEditing = false;
    public descriptionEditableChanged(value: boolean) {
        this.descriptionEditing = value;
        this.setDirty();
    }

    private conversationHasValue = false;
    public conversationValueChanged(value: string) {
        this.conversationHasValue = !!value.trim();
        this.setDirty();
    }

    protected setDirty() {
        this.Base.isDirty = this.descriptionEditing || this.conversationHasValue || this._propertiesTabDirty;
    }

    public $routerCanDeactivate(next?: angular.ComponentInstruction, prev?: angular.ComponentInstruction) {
        if (this.authorizationService.dirty) {
            return this.saveDialog.Save().then(result => {
                let res = result === SaveDialog.Discard;
                return new Promise(resolve => {
                    this.$timeout(25).finally(() => resolve(res));
                });
            }).catch(() => { return false });
        }
        return true;
    }

    protected async AddNewAttachment(file: CaseFileData) {
        if (this.readOnly)
            return;
        this.isProcessingService.StartProcessing();
        try {
            await this.serviceCaseService.AddFile(this.ServiceCase.files, file);
            this.newFileTooltext = this.TranslationService.Translate("FILE_{0}_ADDED")
                .replace("{0}", file.filename);
            this.newFileTooltip = true;
            var startTime = Date.now();
            await Promise.all([,
                this.GetFiles(),
                this.GetHistoryAndCorrespondence()]);
            var elapsedTime = Date.now() - startTime;
            if (elapsedTime > 5000) {
                this.newFileTooltip = false;
                this.newFileTooltext = "";
            } else {
                this.$timeout(() => {
                    this.newFileTooltip = false;
                    this.newFileTooltext = "";
                }, 5000 - elapsedTime)
            }
        } catch (error) {
            if (ServiceUtils.IsUnauthorizedResponse(error))
                return;
            this.application.setSeqUrl(error);
            this.logService.error('ServiceCaseEdit', 'Error adding file', error);
            this.dialogManager.showErrorDialog('error', 'ERROR', 'ADD_ATTACHMENT_CASE_ERROR', { errorResult: error, translate: true });
        } finally {
            this.isProcessingService.StopProcessing()
        }
    }

    protected get ServiceCase(): T {
        return (this.Base) ? this.Base.Data : undefined;
    }

    protected set ServiceCase(data: T) {
        this.Base.Data = data;
        if (data.customerReference === " ") data.customerReference = '';
    }

    protected async AddFeedback(feedback: string, status: FeedbackStatusLink) {
        if (this.readOnlyChat)
            return;
        this.isProcessingService.StartProcessing();
        try {
            let user = await this.application.user;
            await status.act(feedback ? {
                content: feedback,
                createdOn: new Date().toISOString(),
                createdBy: '',
                bySupportEmployee: user.isFiqasEmployee
            } : null);
            this.ajustDynamicHTMLLayout(0);
            await this.ReloadServiceCase();
            this._notchanged = 0;
            if (this._timer !== undefined)
                this.$timeout.cancel(this._timer);
            this._timer = this.$timeout(() => { this.checkEdits(); }, 500);
        } catch (error) {
            if (ServiceUtils.IsUnauthorizedResponse(error))
                return;
            this.application.setSeqUrl(error);
            this.logService.error('ServiceCaseEdit', 'Error adding feedback', error);
            this.dialogManager.showErrorDialog('error', 'ERROR', 'ADD_FEEDBACK_CASE_ERROR', { errorResult: error, translate: true });
        } finally {
            this.isProcessingService.StopProcessing();
        };
    }

    protected async AddCorrespondence(entry: string, reload = true, internal = false) {
        if (this.readOnlyChat)
            return;
        if (!internal && !this.allowExternal)
            internal = true;

        this.showInternal = this.showInternal || internal;
        this.isProcessingService.StartProcessing();
        try {
            let user = await this.application.user;
            await this.serviceCaseService.AddCorrespondence(this.ServiceCase.correspondence, {
                content: entry,
                createdOn: new Date().toISOString(),
                createdBy: '',
                bySupportEmployee: this.authorizationService.currentRoleIn(["SD"]),
                byInternalCustomer: this.authorizationService.lowRole === "CUST" && this.authorizationService.highRole === "FQS",
                byCustomer: this.authorizationService.lowRole === "CUST" && this.authorizationService.highRole === "CUST",
                correspondenceType: internal ? 'internal' : undefined
            });
            this.ajustDynamicHTMLLayout(0);
            if (!this.watched)
                await this.ReloadServiceCase();
            if (reload)
                await this.GetHistoryAndCorrespondence();
            this._notchanged = 0;
            if (this._timer !== undefined)
                this.$timeout.cancel(this._timer);
            this._timer = this.$timeout(() => { this.checkEdits(); }, 500);
        } catch (error) {
            if (ServiceUtils.IsUnauthorizedResponse(error))
                return;
            this.application.setSeqUrl(error);
            this.logService.error('ServiceCaseEdit', 'Error adding correspondence', error);
            this.dialogManager.showErrorDialog('error', 'ERROR', 'ADD_CORRESPONDENCE_CASE_ERROR', { errorResult: error, translate: true });
        } finally {
            this.isProcessingService.StopProcessing();
        }
    }

    public onFileClick(file: FileItem) {
        // Display the file or allow download. Get it from the application server.
        this.fileService.GetFile(file.id).then((file => {
            this.fileService.FileSaveAs(file);
        }));
    }

    public async onFileDelete(file: FileItem) {
        if (this.readOnly)
            return;
        try {
            this.isProcessingService.StartProcessing();
            await this.serviceCaseService.DeleteFile(file.id);
            await Promise.all([
                this.GetFiles(),
                this.GetHistoryAndCorrespondence()]);
        } catch (error) {
            if (ServiceUtils.IsUnauthorizedResponse(error))
                return;
            this.application.setSeqUrl(error);
            this.logService.error('ServiceCaseEdit', 'Error deleting file', error);
            this.dialogManager.showErrorDialog('error', 'ERROR', 'DELETE_ATTACHMENT_CASE_ERROR', { errorResult: error, translate: true });
        } finally {
            this.isProcessingService.StopProcessing();
        }
    }

    public async handleStatus(status: string) {
        if (this.readOnly)
            return;
        if (!this.ServiceCase.actionLinks || !this.ServiceCase.actionLinks.statusLinks || !this.ServiceCase.actionLinks.statusLinks[status])
            return;

        let options: ng.ui.bootstrap.IModalSettings = {
            animation: false,
            templateUrl: 'handle_status_change.html',
            controller: ChangeStatusComponent,
            controllerAs: '$modal',
            resolve: {
                page: () => this
            }
        };

        this.popupTitle = 'POPUP_' + status.toUpperCase() + '_TITLE';
        this.popupText1 = 'POPUP_' + status.toUpperCase() + '_TEXT1';
        this.popupText2 = 'POPUP_' + status.toUpperCase() + '_TEXT2';
        this.popupText3 = 'POPUP_' + status.toUpperCase() + '_TEXT3';
        this.popupEditHint = 'POPUP_' + status.toUpperCase() + '_EDITHINT';

        if (['closed', 'cancelled', 'rework', 'accepted'].filter(x => x === status).length > 0) {
            if (status == "rework") {
                this.commentRequired = true;
                this.statusChangeDisabled = true;
            }
            try {
                let m = this.$modal.open(options);
                let x = await m.result
                if (x === 'yes') {
                    if (this.finalStatusRemark && this.finalStatusRemark.length > 0) {
                        this.isProcessingService.StartProcessing();
                        try {
                            await this.AddCorrespondence(this.finalStatusRemark, false);
                            await this.ServiceCase.actionLinks.statusLinks[status].action();
                            await this.ReloadServiceCase();
                        } catch (error) {
                            this.application.setSeqUrl(error);
                            this.logService.error('ServiceCaseEdit', 'Error updating status', error);
                            this.dialogManager.showErrorDialog('error', 'ERROR', 'EDIT_STATUS_CASE_ERROR', { errorResult: error, translate: true });
                        } finally {
                            this.finalStatusRemark = '';
                            this.isProcessingService.StopProcessing();
                        }
                    } else {
                        try {
                            await this.ServiceCase.actionLinks.statusLinks[status].action();
                            await this.ReloadServiceCase();
                        } catch (error) {
                            this.application.setSeqUrl(error);
                            this.logService.error('ServiceCaseEdit', 'Error updating status', error);
                            this.dialogManager.showErrorDialog('error', 'ERROR', 'EDIT_STATUS_CASE_ERROR', { errorResult: error, translate: true });
                        } finally {
                            this.finalStatusRemark = '';
                            this.isProcessingService.StopProcessing();
                        }
                    }
                }
            } catch (_) { }
        } else {
            this.isProcessingService.StartProcessing();
            try {
                let link = this.ServiceCase.actionLinks.statusLinks[status];
                await link.action();
                await this.ReloadServiceCase();
            } catch (error) {
                if (ServiceUtils.IsUnauthorizedResponse(error))
                    return;
                this.application.setSeqUrl(error);
                this.logService.error('ServiceCaseEdit', 'Error updating status', error);
                this.dialogManager.showErrorDialog('error', 'ERROR', 'EDIT_STATUS_CASE_ERROR', { errorResult: error, translate: true });
            } finally {
                this.finalStatusRemark = '';
                this.isProcessingService.StopProcessing();
            }
        }
    }

    private _finalStatusRemark: string = "";
    public get finalStatusRemark() {
        return this._finalStatusRemark;
    }
    public set finalStatusRemark(val: string) {
        this._finalStatusRemark = val;
        this.statusChangeDisabled = this.commentRequired && (val == undefined || val.length == 0);
    }

    protected popupTitle: string;
    protected popupText1: string;
    protected popupText2: string;
    protected popupText3: string;
    protected popupEditHint: string;
    protected commentRequired: boolean = false;
    protected statusChangeDisabled: boolean = false;


    public copyCaseIdToClipbord() {
        // Select the email link anchor text
        var selectElement = this.$element[0].getElementsByClassName('case-number').item(0);

        var range = document.createRange();
        range.selectNode(selectElement);
        window.getSelection().addRange(range);
    };
}

export class ChangeStatusComponent<T extends ServiceCase> {
    static controllerId = 'ChangeStatusComponent';

    constructor(
        @Inject('$uibModalInstance') private $modalInstance: angular.ui.bootstrap.IModalServiceInstance,
        @Inject('page') public page: ServiceCaseEdit<T>
    ) {
    }

    public Yes() {
        this.$modalInstance.close('yes');
    }

    public No() {
        this.$modalInstance.close('no');
    }

    private remarkChanged(text: string) {
        this.page.finalStatusRemark = text;
    }
}

export class ChangeCorrespondenceTypeComponent {
    static controllerId = 'ChangeCorrespondenceTypeComponent';

    constructor(
        @Inject('$uibModalInstance') private $modalInstance: angular.ui.bootstrap.IModalServiceInstance,
        @Inject('item') public item: { type: CorrespondenceType, text: string, person: string, timestamp: ISODateTimeString }
    ) {
        this.initialValue = item.type;
    }
    public initialValue: CorrespondenceType;

    public Yes() {
        this.$modalInstance.close('yes');
    }

    public No() {
        this.$modalInstance.close('no');
    }

    private onvaluechanged(group: string, value: CorrespondenceType, text: string) {
        this.item.type = value;
    }
}

export class ConfirmDeadlineComponent<T extends ServiceCase> {
    static controllerId = 'ConfirmDeadlineComponent';

    constructor(
        @Inject('$uibModalInstance') private $modalInstance: angular.ui.bootstrap.IModalServiceInstance,
        @Inject('page') public page: ServiceCaseEdit<T>
    ) {
    }

    public Yes() {
        this.$modalInstance.close('yes');
    }

    public No() {
        this.$modalInstance.close('no');
    }

    private remarkChanged(text: string) {
        this.page.deadlineRemark = text;
    }
}
